import axios from "axios";
import config from "../../../../../config.json";
import { DEFAULT_MENU_DISPLAY, DEFAULT_RESTAURANT } from "./WizardVisualSettings";

const STORAGE_VERSION = 1;

export function getDraftStorageKey() {
  const restaurantId = localStorage.getItem("restaurantId") || "default";
  return `startNowWizardDraft:v${STORAGE_VERSION}:${restaurantId}`;
}

export function mergeWizardDraft(saved, initialDraft) {
  if (!saved || typeof saved !== "object") return initialDraft;
  return {
    ...initialDraft,
    ...saved,
    visualSettingsMode: saved.visualSettingsMode === "custom" ? "custom" : "recommended",
    menu: { ...initialDraft.menu, ...(saved.menu || {}) },
    restaurant: { ...initialDraft.restaurant, ...(saved.restaurant || {}) },
    relations: { ...initialDraft.relations, ...(saved.relations || {}) },
    products: Array.isArray(saved.products) ? saved.products : initialDraft.products,
    choices: Array.isArray(saved.choices) ? saved.choices : initialDraft.choices,
    items: Array.isArray(saved.items) ? saved.items : initialDraft.items,
    categories: Array.isArray(saved.categories) ? saved.categories : initialDraft.categories,
    subcategories: Array.isArray(saved.subcategories) ? saved.subcategories : initialDraft.subcategories,
    wizardPath: saved.wizardPath === "static" ? "static" : saved.wizardPath === "interactive" ? "interactive" : null,
  };
}

export function loadWizardDraft() {
  try {
    const raw = localStorage.getItem(getDraftStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

const MAX_PERSISTED_DATA_URL_CHARS = 2_500_000;

function persistDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (dataUrl.length > MAX_PERSISTED_DATA_URL_CHARS) return null;
  return dataUrl;
}

export function saveWizardDraft({
  draft,
  activeStep,
  menuPhotoDataUrl,
  restaurantLogoDataUrl,
  productPhotos,
  subcategoryBanners,
  staticMenuDataUrl,
  wizardPath,
  migratedTo8Steps = true,
  migratedTo10Steps = true,
  migratedTo9Steps = true,
  migratedConfirmIntoPreview = true,
  migratedCategoriesBeforeProducts = true,
  migratedLinkIntoProducts = true,
}) {
  try {
    localStorage.setItem(
      getDraftStorageKey(),
      JSON.stringify({
        draft,
        activeStep,
        menuPhotoDataUrl: persistDataUrl(menuPhotoDataUrl),
        restaurantLogoDataUrl: persistDataUrl(restaurantLogoDataUrl),
        productPhotos: productPhotos && typeof productPhotos === "object" ? productPhotos : {},
        subcategoryBanners:
          subcategoryBanners && typeof subcategoryBanners === "object" ? subcategoryBanners : {},
        staticMenuDataUrl: persistDataUrl(staticMenuDataUrl),
        wizardPath: wizardPath === "static" || wizardPath === "interactive" ? wizardPath : null,
        migratedTo8Steps,
        migratedTo10Steps,
        migratedTo9Steps,
        migratedConfirmIntoPreview,
        migratedCategoriesBeforeProducts,
        migratedLinkIntoProducts,
        savedAt: Date.now(),
      })
    );
    return true;
  } catch {
    return false;
  }
}

export function clearWizardDraft() {
  localStorage.removeItem(getDraftStorageKey());
}

export function hasStoredWizardDraft() {
  return !!loadWizardDraft();
}

export function computeWizardProgressPct(stepComplete) {
  if (!Array.isArray(stepComplete) || stepComplete.length === 0) return 0;
  const done = stepComplete.filter(Boolean).length;
  return Math.round((done / stepComplete.length) * 100);
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function mimeToExtension(mime = "") {
  const normalized = String(mime).toLowerCase();
  if (normalized === "image/webp") return "webp";
  if (normalized === "image/jpeg" || normalized === "image/jpg") return "jpg";
  if (normalized === "image/png") return "png";
  if (normalized === "application/pdf") return "pdf";
  return "bin";
}

export function buildStaticMenuFilename(mediaType, mime = "") {
  if (mediaType === "pdf") return "static-menu.pdf";
  const ext = mimeToExtension(mime);
  return `static-menu.${ext === "bin" ? "webp" : ext}`;
}

export function dataUrlToFile(dataUrl, filename = "menu-logo.webp") {
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
    return null;
  }
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) return null;

  const header = dataUrl.slice(0, commaIndex);
  const base64 = dataUrl.slice(commaIndex + 1);
  const mime = header.match(/data:([^;]+)/)?.[1] || "image/webp";
  const safeName =
    filename && filename.includes(".")
      ? filename
      : `${filename}.${mimeToExtension(mime)}`;

  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], safeName, { type: mime });
  } catch {
    return null;
  }
}

export async function blobUrlToFile(previewUrl, filename = "upload.bin", mime = "") {
  if (!previewUrl || typeof previewUrl !== "string" || !previewUrl.startsWith("blob:")) {
    return null;
  }
  try {
    const response = await fetch(previewUrl);
    const blob = await response.blob();
    const type = blob.type || mime || "application/octet-stream";
    const safeName =
      filename && filename.includes(".")
        ? filename
        : `${filename}.${mimeToExtension(type)}`;
    return new File([blob], safeName, { type });
  } catch {
    return null;
  }
}

export async function dataUrlToFileAsync(dataUrl, filename = "menu-logo.webp") {
  const syncFile = dataUrlToFile(dataUrl, filename);
  if (syncFile) return syncFile;
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
    return null;
  }
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const type = blob.type || "image/webp";
    const safeName =
      filename && filename.includes(".")
        ? filename
        : `${filename}.${mimeToExtension(type)}`;
    return new File([blob], safeName, { type });
  } catch {
    return null;
  }
}

export async function resolveWizardUploadFile({
  file,
  dataUrl,
  previewUrl,
  defaultName = "upload.webp",
  fallbackMime = "",
}) {
  if (file instanceof File && file.size > 0) {
    return file;
  }
  if (dataUrl) {
    const fromDataUrl = await dataUrlToFileAsync(dataUrl, defaultName);
    if (fromDataUrl) return fromDataUrl;
  }
  if (previewUrl) {
    return blobUrlToFile(previewUrl, defaultName, fallbackMime);
  }
  return null;
}

function collectAssignedProductIds(relations) {
  const assigned = new Set();
  Object.values(relations?.categoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  Object.values(relations?.subcategoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  return assigned;
}

export async function uploadWizardProductPhotos({ products, productIds, productPhotos, token }) {
  const uploads = [];
  const list = Array.isArray(products) ? products : [];
  const ids = Array.isArray(productIds) ? productIds : [];

  for (let i = 0; i < list.length; i += 1) {
    const tempId = list[i]?.tempId;
    const productId = ids[i];
    const dataUrl = tempId ? productPhotos?.[tempId] : null;
    if (!productId || !dataUrl) continue;

    const file = await dataUrlToFileAsync(dataUrl, `product-${productId}.webp`);
    if (!file) continue;

    const formData = new FormData();
    formData.append("file", file);
    uploads.push(
      axios.post(`${config.dev}/v2/products/photos/${productId}`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
    );
  }

  if (uploads.length === 0) return;
  await Promise.all(uploads);
}

export async function uploadWizardSubcategoryBanners({
  subcategories,
  subcategoryIds,
  subcategoryBanners,
  token,
}) {
  const uploads = [];
  const list = Array.isArray(subcategories) ? subcategories : [];
  const ids = Array.isArray(subcategoryIds) ? subcategoryIds : [];

  for (let i = 0; i < list.length; i += 1) {
    const tempId = list[i]?.tempId;
    const subcategoryId = ids[i];
    const dataUrl = tempId ? subcategoryBanners?.[tempId] || list[i]?.bannerUrl : null;
    if (!subcategoryId || !dataUrl) continue;

    const file = await dataUrlToFileAsync(dataUrl, `subcategory-${subcategoryId}.webp`);
    if (!file) continue;

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        bannerImageDisabled: list[i]?.bannerImageDisabled === true,
      })
    );
    formData.append("file", file);
    uploads.push(
      axios.put(`${config.dev}/v2/subcategories/update/${subcategoryId}`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
    );
  }

  if (uploads.length === 0) return;
  await Promise.all(uploads);
}

export function prepareWizardDraftForSubmit(draft) {
  const relations = { ...(draft?.relations || {}) };
  const categories = Array.isArray(draft?.categories) ? draft.categories : [];

  if (!Array.isArray(relations.menuCategories) || relations.menuCategories.length === 0) {
    relations.menuCategories = categories.map((c) => c.tempId).filter(Boolean);
  }

  const categoryProducts = { ...(relations.categoryProducts || {}) };
  relations.menuCategories.forEach((cid) => {
    if (!Array.isArray(categoryProducts[cid])) categoryProducts[cid] = [];
  });
  relations.categoryProducts = categoryProducts;

  const next = { ...draft, relations };
  if (draft?.visualSettingsMode !== "custom") {
    next.visualSettingsMode = "recommended";
    next.restaurant = { ...DEFAULT_RESTAURANT };
    next.menu = { ...(draft?.menu || {}), ...DEFAULT_MENU_DISPLAY };
  }
  return next;
}

export function getWizardSubmitIssues(draft, t, options = {}) {
  const errors = [];
  const warnings = [];
  const menuDetails = Array.isArray(draft?.menu?.menuDetails) ? draft.menu.menuDetails : [];
  const primaryLang = draft?.menu?.primaryLanguage || "en";
  const menuName = menuDetails.find((d) => d?.language === primaryLang)?.menuName?.trim() || "";
  const isStatic =
    options.wizardPath === "static" || draft?.wizardPath === "static" || draft?.menu?.menuContentType === "static";

  if (!menuName) errors.push(t("wizardConfirmErrorMenu"));

  if (isStatic) {
    if (!options.hasStaticFile) {
      errors.push(t("wizardConfirmErrorStaticFile"));
    }
    return {
      errors,
      warnings,
      canSubmit: errors.length === 0,
    };
  }

  const products = Array.isArray(draft?.products) ? draft.products : [];
  const categories = Array.isArray(draft?.categories) ? draft.categories : [];
  const relations = draft?.relations || {};
  const assigned = collectAssignedProductIds(relations);

  if (products.length === 0) errors.push(t("wizardConfirmErrorProducts"));
  if (categories.length === 0) errors.push(t("wizardConfirmErrorCategories"));

  const unassigned = products.filter((p) => !assigned.has(p.tempId)).length;
  if (products.length > 0 && unassigned > 0) {
    errors.push(t("wizardConfirmErrorLinks"));
  }

  return {
    errors,
    warnings,
    canSubmit: errors.length === 0,
  };
}

export function formatWizardSubmitError(error, t) {
  if (!error) return t("wizardConfirmErrorGeneric");
  if (typeof error === "string") return error;
  if (error?.error && typeof error.error === "string") return error.error;
  if (error?.message) return String(error.message);
  return t("wizardConfirmErrorGeneric");
}
